import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getImageUrl } from 'constants/constants';
import {
  getProductDashboardItems,
  getProductListByBrand,
  getProductSizes,
  searchProductList,
  searchByTextProductList,
} from 'helpers/backend_helper';
import {
  Filter,
  Search,
  ChevronDown,
  Grid3X3,
  LayoutList,
  Plus,
  X,
  Check,
} from 'lucide-react';

type FilterMenuId = 'shopFor' | 'categories' | 'brands' | 'sizes';

const SHOP_FOR_OPTIONS = [
  { id: 'All', name: 'All' },
  { id: 'Boys', name: 'Boys' },
  { id: 'Girls', name: 'Girls' },
  { id: 'Unisex', name: 'Unisex' },
];

const SORT_OPTIONS = [
  { id: 'newest', name: 'Newest First' },
  { id: 'price-asc', name: 'Price: Low to High' },
  { id: 'price-desc', name: 'Price: High to Low' },
  { id: 'name-asc', name: 'Name: A-Z' },
];

interface FilterState {
  categories: string[];
  brands: string[];
  sizes: string[];
  shopFor: string;
}

const MENU_ITEMS: { id: FilterMenuId; label: string }[] = [
  { id: 'shopFor', label: 'Shop For' },
  { id: 'categories', label: 'Categories' },
  { id: 'brands', label: 'Brands' },
  { id: 'sizes', label: 'Size' },
];

const MIN_SEARCH_LENGTH = 2;

/** Map API response to display format. Handles both:
 * - customsearch/textsearch (plentima-b2b): productId, productName, imageUrl[], sizeNames, colors
 * - getProductListByBrand (product templates): id, name, productsProductvariants[]
 */
function mapApiProductToDisplay(item: any, index: number): any {
  const variant = item.productsProductvariants?.[0];
  const isApiFormat = item.productId != null || item.productName != null;

  if (isApiFormat) {
    const images = item.imageUrl ?? [];
    const firstImage = Array.isArray(images) ? images[0] : images;
    const imageUrl = firstImage || null;
    const netPrice = item.netPrice ?? item.unitPrice ?? 0;
    const unitPrice = item.unitPrice ?? netPrice;
    const sizeNames = item.sizeNames ?? [];
    const colors = item.colors ?? [];

    return {
      id: String(item.productId ?? item.id ?? index),
      name: item.productName ?? item.name ?? item.brandName ?? 'Unknown',
      categoryName: item.categoryName,
      categoryId: item.categoryId,
      brandId: item.brandId,
      brandName: item.brandName,
      imageUrl,
      unitPrice: Number(netPrice || unitPrice),
      netPrice: Number(netPrice),
      discountValue: item.discountValue ?? 0,
      packOf: item.packOf ?? 1,
      sizeNames,
      colors,
      mrp: item.mrp,
      margin: item.margin,
      inventory: item.inventory ?? 0,
      productsProductvariants: [{
        unitPrice: Number(netPrice || unitPrice),
        discountValue: item.discountValue ?? 0,
        packOf: item.packOf ?? 1,
        imageUrl,
        productsizesProductvariants: { name: sizeNames[0] || 'NA' },
        colorCode: colors[0],
      }],
    };
  }

  return item;
}

const CustomerProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const brandId = searchParams.get('brandId') || '';
  const categoryId = searchParams.get('categoryId') || '';

  const [products, setProducts] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [sizes, setSizes] = useState<any[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    setSearchInput(search);
  }, [search]);
  const [sortBy, setSortBy] = useState('newest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<FilterMenuId>('shopFor');
  const [filters, setFilters] = useState<FilterState>({
    categories: categoryId ? [categoryId] : [],
    brands: brandId ? [brandId] : [],
    sizes: [],
    shopFor: 'All',
  });
  const [localFilters, setLocalFilters] = useState<FilterState>(filters);

  const buildApiFilterPayload = useCallback(() => {
    return {
      colors_param: [] as string[],
      categoryIds_param: filters.categories,
      sizeIds_param: filters.sizes,
      brandIds_param: filters.brands,
      seasonIds_param: [] as string[],
      min_price: '10',
      max_price: '3000',
      gender_param: filters.shopFor ?? 'All',
    };
  }, [filters]);

  const loadProducts = useCallback(
    async (type: 'initial' | 'search' | 'filter') => {
      setIsLoading(true);
      try {
        let payload = buildApiFilterPayload();
        const searchTxt = (type === 'search' && search) ? search : searchInput;

        if (type === 'initial' || type === 'filter') {
          if (categoryId) payload.categoryIds_param = [categoryId];
          if (brandId) payload.brandIds_param = [brandId];
        }
        if (type === 'search' && searchTxt.length < MIN_SEARCH_LENGTH) {
          setIsLoading(false);
          return;
        }

        let response;
        if (type === 'search' && searchTxt.length >= MIN_SEARCH_LENGTH) {
          response = await searchByTextProductList(searchTxt);
        } else {
          try {
            response = await searchProductList(payload);
          } catch (err) {
            // Fallback: if customsearch API not available, use getProductListByBrand
            const bid = payload.brandIds_param?.[0] || brandId;
            if (bid) {
              const fallback = await getProductListByBrand(String(bid));
              if (fallback.success && fallback.data) {
                const list = Array.isArray(fallback.data) ? fallback.data : [fallback.data];
                setProducts(list.map((item: any, i: number) => mapApiProductToDisplay(item, i)));
              } else {
                setProducts([]);
              }
            } else {
              setProducts([]);
            }
            setIsLoading(false);
            return;
          }
        }

        const rows = response?.data?.rows ?? response?.data ?? [];
        const list = Array.isArray(rows) ? rows : [];
        const mapped = list.map((item: any, i: number) => mapApiProductToDisplay(item, i));
        setProducts(mapped);
      } catch (error) {
        console.error('Error loading products', error);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    },
    [buildApiFilterPayload, searchInput, search, categoryId, brandId]
  );

  useEffect(() => {
    const loadMasterData = async () => {
      const [dashboardRes, sizesRes] = await Promise.all([
        getProductDashboardItems(),
        getProductSizes(),
      ]);
      const data = dashboardRes.success && dashboardRes.data?.[0] ? dashboardRes.data[0] : {};
      setBrands(data.brands || []);
      setCategories(data.categories || []);
      setSizes(sizesRes.success && sizesRes.data ? sizesRes.data : []);

      const bid = brandId ? parseInt(brandId, 10) : data.brands?.[0]?.id;
      if (bid && data.brands) {
        setSelectedBrand(data.brands.find((b: any) => b.id === bid) || data.brands[0]);
      }
    };
    loadMasterData();
  }, [brandId]);

  useEffect(() => {
    setLocalFilters((prev) => ({
      ...prev,
      categories: categoryId ? [categoryId] : prev.categories,
      brands: brandId ? [brandId] : prev.brands,
    }));
    setFilters((prev) => ({
      ...prev,
      categories: categoryId ? [categoryId] : prev.categories,
      brands: brandId ? [brandId] : prev.brands,
    }));
  }, [categoryId, brandId]);

  useEffect(() => {
    if (search && search.length >= MIN_SEARCH_LENGTH) {
      loadProducts('search');
    } else {
      loadProducts('filter');
    }
  }, [filters, search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchInput.trim()) params.set('search', searchInput.trim());
    else params.delete('search');
    setSearchParams(params);
  };

  const filteredAndSortedProducts = useMemo(() => {
    const list = [...products];

    switch (sortBy) {
      case 'price-asc':
        list.sort((a: any, b: any) => {
          const pa = a.productsProductvariants?.[0]?.unitPrice ?? a.unitPrice ?? 0;
          const pb = b.productsProductvariants?.[0]?.unitPrice ?? b.unitPrice ?? 0;
          return pa - pb;
        });
        break;
      case 'price-desc':
        list.sort((a: any, b: any) => {
          const pa = a.productsProductvariants?.[0]?.unitPrice ?? a.unitPrice ?? 0;
          const pb = b.productsProductvariants?.[0]?.unitPrice ?? b.unitPrice ?? 0;
          return pb - pa;
        });
        break;
      case 'name-asc':
        list.sort((a: any, b: any) =>
          (a.name || '').localeCompare(b.name || '')
        );
        break;
      default:
        break;
    }

    return list;
  }, [products, sortBy]);

  const toggleFilter = (type: keyof FilterState, id: string) => {
    if (type === 'shopFor') {
      setLocalFilters({ ...localFilters, shopFor: id });
      return;
    }
    const arr = localFilters[type] as string[];
    const newArr = arr.includes(id)
      ? arr.filter((x) => x !== id)
      : [...arr, id];
    setLocalFilters({ ...localFilters, [type]: newArr });
  };

  const applyFilters = () => {
    setFilters(localFilters);
    setFilterPanelOpen(false);
  };

  const resetFilters = () => {
    const reset = {
      categories: [],
      brands: brandId ? [brandId] : [],
      sizes: [],
      shopFor: 'All',
    };
    setLocalFilters(reset);
    setFilters(reset);
  };

  const renderProductCard = (product: any) => {
    const variants = product?.productsProductvariants || [];
    const firstVariant = variants[0];
    const imageUrl =
      product?.imageUrl ||
      firstVariant?.imageUrl ||
      product?.productsProductimages?.[0]?.url;
    const unitPrice = firstVariant?.unitPrice ?? product.unitPrice ?? 0;
    const discountPercent = firstVariant?.discountPercent ?? 0;
    const discountValue = firstVariant?.discountValue ?? 0;
    const originalPrice =
      discountPercent > 0 ? unitPrice / (1 - discountPercent / 100) : unitPrice;
    const packOf = firstVariant?.packOf || 1;
    const sizeName =
      firstVariant?.productsizesProductvariants?.name || variants[0]?.sizeName || 'NA';
    const colors = [
      ...new Set(
        variants
          .map((v: any) => v.colorCode)
          .filter(Boolean)
      ),
    ] as string[];

    return (
      <div
        key={product.id}
        className="group overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition hover:border-custom-500 hover:shadow-md dark:border-zink-500 dark:bg-zink-700"
      >
        <Link to={`/customer/products/${product.id}`}>
          <div className="relative aspect-square overflow-hidden bg-slate-100 dark:bg-zink-600">
            {imageUrl ? (
              <img
                src={getImageUrl(imageUrl, 'productThumbnail')}
                alt={product.name}
                className="h-full w-full object-cover transition group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-slate-400">
                <span className="text-4xl">{product.name?.charAt(0) || '?'}</span>
              </div>
            )}
            {discountValue > 0 && (
              <span className="absolute left-2 top-2 rounded bg-red-500 px-2 py-0.5 text-xs font-medium text-white">
                OFF ₹{Math.round(discountValue)}
              </span>
            )}
          </div>
        </Link>
        <div className="p-3">
          <Link to={`/customer/products/${product.id}`}>
            <p className="truncate text-sm font-medium uppercase text-slate-800 dark:text-zink-100">
              {product.name}
            </p>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-zink-400">
              {product.categoriesProducts?.name || product.categoryName || '—'}
            </p>
          </Link>
          <div className="mt-2 flex items-center gap-2">
            {originalPrice > unitPrice && (
              <span className="text-sm text-slate-400 line-through">
                ₹{Math.round(originalPrice)}
              </span>
            )}
            <span className="text-sm font-semibold text-green-600 dark:text-green-400">
              ₹{Number(unitPrice).toFixed(2)}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Pack Of: {packOf}Pc
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Size: {sizeName}
            {variants.length > 1 && (
              <span className="text-slate-400"> +{variants.length - 1} option(s)</span>
            )}
          </p>
          {colors.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1">
              {colors.slice(0, 5).map((c: string, i: number) => (
                <span
                  key={i}
                  className="inline-block h-3 w-3 rounded-full border border-slate-200"
                  style={{ backgroundColor: c || '#ccc' }}
                  title={c}
                />
              ))}
              {colors.length > 5 && (
                <span className="text-xs text-slate-400">+{colors.length - 5}</span>
              )}
            </div>
          )}
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-slate-500">MRP: NA</p>
            <Link
              to={`/customer/products/${product.id}`}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-custom-500 text-white transition hover:bg-custom-600"
              onClick={(e) => e.stopPropagation()}
            >
              <Plus className="size-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  };

  const renderFilterOptions = () => {
    if (selectedMenu === 'shopFor') {
      return SHOP_FOR_OPTIONS.map((opt) => {
        const selected = localFilters.shopFor === opt.id;
        return (
          <button
            key={opt.id}
            onClick={() => toggleFilter('shopFor', opt.id)}
            className={`flex w-full items-center gap-2 border-b border-slate-200 py-3 px-4 text-left text-sm dark:border-zink-500 ${
              selected ? 'font-semibold text-custom-500' : 'text-slate-700 dark:text-zink-200'
            }`}
          >
            {selected && <Check className="size-4 shrink-0" />}
            {opt.name}
          </button>
        );
      });
    }
    if (selectedMenu === 'categories') {
      return categories.map((cat: any) => {
        const id = String(cat.id);
        const selected = localFilters.categories.includes(id);
        return (
          <button
            key={cat.id}
            onClick={() => toggleFilter('categories', id)}
            className={`flex w-full items-center gap-2 border-b border-slate-200 py-3 px-4 text-left text-sm dark:border-zink-500 ${
              selected ? 'font-semibold text-custom-500' : 'text-slate-700 dark:text-zink-200'
            }`}
          >
            {selected && <Check className="size-4 shrink-0" />}
            {cat.name}
          </button>
        );
      });
    }
    if (selectedMenu === 'brands') {
      return brands.map((b: any) => {
        const id = String(b.id);
        const selected = localFilters.brands.includes(id);
        return (
          <button
            key={b.id}
            onClick={() => toggleFilter('brands', id)}
            className={`flex w-full items-center gap-2 border-b border-slate-200 py-3 px-4 text-left text-sm dark:border-zink-500 ${
              selected ? 'font-semibold text-custom-500' : 'text-slate-700 dark:text-zink-200'
            }`}
          >
            {selected && <Check className="size-4 shrink-0" />}
            {b.name}
          </button>
        );
      });
    }
    if (selectedMenu === 'sizes') {
      const sizeList = sizes.length > 0 ? sizes : [];
      return sizeList.map((s: any) => {
        const id = String(s.id);
        const selected = localFilters.sizes.includes(id);
        return (
          <button
            key={s.id}
            onClick={() => toggleFilter('sizes', id)}
            className={`flex w-full items-center gap-2 border-b border-slate-200 py-3 px-4 text-left text-sm dark:border-zink-500 ${
              selected ? 'font-semibold text-custom-500' : 'text-slate-700 dark:text-zink-200'
            }`}
          >
            {selected && <Check className="size-4 shrink-0" />}
            {s.name}
          </button>
        );
      });
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="size-12 animate-spin rounded-full border-4 border-custom-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Page header */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-800 dark:text-zink-100">
          Brand: {selectedBrand?.name || 'All'}
        </h1>
        <Link
          to="/customer/cart"
          className="relative flex items-center justify-center rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-zink-200 dark:hover:bg-zink-600"
        >
          <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-custom-500 text-xs font-medium text-white">
            0
          </span>
        </Link>
      </div>

      {/* Filter, Search, Sort bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-2 dark:border-zink-500 dark:bg-zink-700">
        <button
          onClick={() => setFilterPanelOpen(true)}
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-zink-500 dark:text-zink-200 dark:hover:bg-zink-600"
        >
          <Filter className="size-4" />
          Filter
        </button>
        <form onSubmit={handleSearch} className="flex-1 min-w-[120px]">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search..."
              className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-custom-500 focus:outline-none focus:ring-1 focus:ring-custom-500 dark:border-zink-500 dark:bg-zink-700 dark:text-zink-100"
            />
          </div>
        </form>
        <div className="relative">
          <button
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 dark:border-zink-500 dark:text-zink-200"
          >
            <ChevronDown className="size-4" />
            Sort
          </button>
          {showSortDropdown && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowSortDropdown(false)}
              />
              <div className="absolute right-0 top-full z-20 mt-1 min-w-[180px] rounded-lg border border-slate-200 bg-white py-2 shadow-lg dark:border-zink-500 dark:bg-zink-700">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      setSortBy(opt.id);
                      setShowSortDropdown(false);
                    }}
                    className={`block w-full px-4 py-2 text-left text-sm ${
                      sortBy === opt.id
                        ? 'font-semibold text-custom-500'
                        : 'text-slate-700 dark:text-zink-200'
                    }`}
                  >
                    {opt.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-1 dark:border-zink-500">
          <button
            onClick={() => setViewMode('grid')}
            className={`rounded p-1.5 ${
              viewMode === 'grid'
                ? 'bg-custom-500 text-white'
                : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-zink-600'
            }`}
          >
            <Grid3X3 className="size-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`rounded p-1.5 ${
              viewMode === 'list'
                ? 'bg-custom-500 text-white'
                : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-zink-600'
            }`}
          >
            <LayoutList className="size-4" />
          </button>
        </div>
      </div>

      {/* Results count */}
      <p className="mb-3 text-sm text-slate-500 dark:text-zink-400">
        {filteredAndSortedProducts.length} product(s)
      </p>

      {/* Product grid */}
      <div
        className={
          viewMode === 'grid'
            ? 'grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
            : 'flex flex-col gap-3'
        }
      >
        {filteredAndSortedProducts.map((product) =>
          viewMode === 'list' ? (
            <div key={product.id} className="flex gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-zink-500 dark:bg-zink-700">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                {product?.imageUrl || product?.productsProductvariants?.[0]?.imageUrl ? (
                  <img
                    src={getImageUrl(
                      product.imageUrl || product.productsProductvariants[0].imageUrl,
                      'productThumbnail'
                    )}
                    alt={product.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-2xl text-slate-400">
                    {product.name?.charAt(0) || '?'}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <Link to={`/customer/products/${product.id}`}>
                  <p className="font-medium text-slate-800 dark:text-zink-100">
                    {product.name}
                  </p>
                </Link>
                <p className="text-sm text-slate-500">
                  {product.categoriesProducts?.name || '—'}
                </p>
                <p className="mt-1 font-semibold text-green-600">
                  ₹{product.productsProductvariants?.[0]?.unitPrice ?? product.unitPrice ?? '—'}
                </p>
              </div>
              <Link
                to={`/customer/products/${product.id}`}
                className="flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-full bg-custom-500 text-white"
              >
                <Plus className="size-4" />
              </Link>
            </div>
          ) : (
            renderProductCard(product)
          )
        )}
      </div>

      {filteredAndSortedProducts.length === 0 && (
        <section className="rounded-lg border border-dashed border-slate-300 py-16 text-center text-slate-500 dark:border-zink-500 dark:text-zink-400">
          No products found.
        </section>
      )}

      {/* Filter panel modal */}
      {filterPanelOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setFilterPanelOpen(false)}
          />
          <div className="relative flex h-[80vh] w-full max-w-2xl flex-col rounded-t-2xl bg-white shadow-xl dark:bg-zink-700 sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-zink-500">
              <h3 className="font-semibold text-slate-800 dark:text-zink-100">
                Brand: {selectedBrand?.name || 'All'}
              </h3>
              <button
                onClick={() => setFilterPanelOpen(false)}
                className="rounded p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-zink-600"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="flex flex-1 min-h-0">
              <div className="w-[40%] shrink-0 border-r border-slate-200 dark:border-zink-500">
                {MENU_ITEMS.map((item) => {
                  const isSelected = selectedMenu === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setSelectedMenu(item.id)}
                      className={`flex w-full items-center px-4 py-3 text-left text-sm ${
                        isSelected
                          ? 'border-l-4 border-custom-500 font-semibold text-slate-800 dark:text-zink-100'
                          : 'text-slate-500 dark:text-zink-400'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex-1 overflow-y-auto">
                {renderFilterOptions()}
              </div>
            </div>
            <div className="flex gap-3 border-t border-slate-200 px-4 py-4 dark:border-zink-500">
              <button
                onClick={resetFilters}
                className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-custom-500 hover:bg-slate-50 dark:border-zink-500 dark:hover:bg-zink-600"
              >
                Reset
              </button>
              <button
                onClick={applyFilters}
                className="flex-[2] rounded-lg bg-custom-500 py-2.5 text-sm font-medium text-white hover:bg-custom-600"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerProducts;
